<script setup lang="ts">
import { computed, inject, onMounted } from "vue";
import { EnvironmentIndicatorFormElement, FormElementType } from "~/api";
import {
  FormResponseState,
  formResponseInjectKey,
} from "~/NewFormBuilder/composables/useFormResponse";
import { FormDisplayMode } from "~/NewFormBuilder/types/FormDisplayMode";
import { getEnvConfig } from "~/services/appConfig";
import { capitalizeWords } from "~/utils/miscUtils";
import { DefaultFormComponentProps } from "./FormComponentRenderer.vue";

const props =
  defineProps<DefaultFormComponentProps<EnvironmentIndicatorFormElement>>();

const { useAnswer, form } = inject(formResponseInjectKey) as FormResponseState;
const answer = useAnswer<FormElementType.ENVIRONMENT_INDICATOR>(
  props.modelValue,
);

/**
 * Optional companion element (typically an "Affected system" SelectList) that gets
 * seeded with the same environment value. Resolved once at setup — useAnswer must
 * not be called conditionally or inside a lifecycle hook.
 *
 * Looked up by question code across the flat component list, which does not account
 * for repeater instances; this element is not intended for use inside a repeater.
 */
const seedTarget = computed(() => {
  const code = props.modelValue.properties.seedEnvironmentIntoQuestionCode;
  return code
    ? form.components.find((c) => c.questionCode === code)
    : undefined;
});

const seedTargetAnswer = seedTarget.value
  ? useAnswer<FormElementType.SELECT_LIST>(seedTarget.value)
  : undefined;

/**
 * The environment this browser session is running against. In dev the runtime
 * config is never fetched from /envConfig, so getEnvConfig() throws.
 */
const currentEnvironment = (): string => {
  if (import.meta.env.DEV) {
    return "development";
  }
  try {
    return getEnvConfig().env;
  } catch {
    return "unknown";
  }
};

/**
 * Stamp the environment the first time this response is opened, and never again.
 * The stored answer is what makes the value "freeze" — a response created on a
 * test system still reports that system when later opened in live. Responses created
 * before this element was added have no stamp and are backfilled on first view,
 * which is the closest we can get for them.
 */
onMounted(() => {
  const env = currentEnvironment();

  // Gate on the form's display mode, NOT props.isReadOnly: this element sets
  // isReadOnly on itself (the value is machine-stamped, never user-typed), so
  // props.isReadOnly is always true here and would block the stamp entirely.
  const canWrite = props.displayMode !== FormDisplayMode.READ_ONLY;

  if (!answer.value && canWrite) {
    answer.value = env;
  }

  // Seed the companion element only while it is still empty, so a user's later
  // choice is never overwritten on subsequent opens.
  if (canWrite && seedTargetAnswer && !seedTargetAnswer.value) {
    seedTargetAnswer.value = env;
  }
});

// Always prefer the stamped value; only fall back while the stamp is being written.
const environment = computed(() => answer.value || currentEnvironment());

const isLive = computed(() => environment.value === "live");

const hidden = computed(
  () => isLive.value && !!props.modelValue.properties.hideWhenLive,
);

const displayText = computed(() => {
  const name = capitalizeWords(environment.value);
  const prefix = props.modelValue.properties.prefix;
  return prefix ? `${prefix} ${name}` : name;
});
</script>

<template>
  <div v-if="!hidden" class="EnvironmentIndicatorComponent">
    <slot name="label" :element="modelValue" />
    <span
      class="environment-badge"
      :class="[`environment-${environment}`, { 'is-live': isLive }]"
    >
      {{ displayText }}
    </span>
  </div>
</template>

<style lang="less" scoped>
.EnvironmentIndicatorComponent {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  .environment-badge {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 10px;
    font-size: 0.85em;
    font-weight: 600;
    letter-spacing: 0.02em;
    // Default (non-live) styling: draw attention.
    color: var(--el-color-warning);
    background-color: var(--el-color-warning-light-9);
    border: 1px solid var(--el-color-warning-light-5);

    &.is-live {
      color: var(--el-color-success);
      background-color: var(--el-color-success-light-9);
      border-color: var(--el-color-success-light-5);
    }
  }
}
</style>
